import type { WebStorage } from "redux-persist";

// redux-persist/lib/storage/session's CJS default export gets double-wrapped
// by Vite/esbuild's CJS interop (storageSession.default.default is the real
// storage, storageSession.getItem is undefined), crashing persistStore on
// load. The adapter is a few lines over sessionStorage, so implement it
// directly instead of depending on that deep subpath import.
//
// window.sessionStorage can throw SecurityError (Safari private browsing,
// storage blocked by browser settings) or ReferenceError (window undefined,
// e.g. this module loaded outside a browser). Either would crash the store
// the same way the bug above did, so probe availability up front and fall
// back to an in-memory store -- redux-persist's own storage engines do the
// same feature-test-then-fallback for this reason.
function createInMemoryStorage(): WebStorage {
  const store = new Map<string, string>();
  return {
    getItem: (key) => Promise.resolve(store.get(key) ?? null),
    setItem: (key, value) => {
      store.set(key, value);
      return Promise.resolve();
    },
    removeItem: (key) => {
      store.delete(key);
      return Promise.resolve();
    },
  };
}

function browserSessionStorageIsAvailable(): boolean {
  try {
    const probeKey = "__gyftpro_admin_storage_probe__";
    window.sessionStorage.setItem(probeKey, probeKey);
    window.sessionStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

export const sessionWebStorage: WebStorage = browserSessionStorageIsAvailable()
  ? {
      getItem: (key) => Promise.resolve(window.sessionStorage.getItem(key)),
      setItem: (key, value) => {
        window.sessionStorage.setItem(key, value);
        return Promise.resolve();
      },
      removeItem: (key) => {
        window.sessionStorage.removeItem(key);
        return Promise.resolve();
      },
    }
  : createInMemoryStorage();
